import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { planKeys } from '@/services/queryKeys';
import {
  planService,
  type BillingPaymentsResponse,
  type PaymentHistoryItem,
  type PlanInfo,
} from '@/services/plan.service';
import type { PlanFeature } from '@erpgold/shared';
import { setPlan } from '@/redux/slices/planSlice';
import type { AppDispatch } from '@/redux';
import { isArrayResponse, withMockFallback } from '@/utils/queryMock';

async function mockPlan(): Promise<PlanInfo> {
  await new Promise((r) => setTimeout(r, 150));
  // Mock: enterprise tier để mọi FeatureGate pass + không trigger LimitWarning.
  // Khi BE ready, query trả plan thật, mock này bị skip.
  return {
    tier: 'enterprise',
    expires_at: null,
    trial_days_left: null,
    grace_days_left: null,
    features: ['*' as unknown as PlanFeature],
    usage: {
      products: 0,
      staffs: 0,
      stores: 0,
      monthly_orders: 0,
    },
  };
}

function isValidPlanInfo(v: unknown): v is PlanInfo {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return typeof o.tier === 'string' && typeof o.usage === 'object' && o.usage !== null;
}

  // EG-80 — Transform JSON snake_case (BE contract per ADR-013) sang camelCase
// Redux internal state. ADR-013 cấm mapping ở response/DTO layer; mapping
// service-DTO → state ở đây là FE-internal modeling, KHÔNG phải transform
// API contract.
function toPlanState(p: PlanInfo) {
  return {
    tier: p.tier,
    expiresAt: p.expires_at,
    trialDaysLeft: p.trial_days_left,
    graceDaysLeft: p.grace_days_left,
    features: p.features ?? [],
    usage: {
      products: p.usage.products,
      staffs: p.usage.staffs,
      stores: p.usage.stores,
      monthlyOrders: p.usage.monthly_orders,
    },
  };
}

/**
 * Load plan info + sync to Redux on mount.
 * Fallback mock khi BE fail hoặc shape lạ.
 */
export function usePlanData() {
  const dispatch = useDispatch<AppDispatch>();
  const query = useQuery({
    queryKey: planKeys.current(),
    queryFn: () =>
      withMockFallback(() => planService.current(), mockPlan, {
        label: 'plan.current',
        validate: isValidPlanInfo,
      }),
    retry: 0,
  });

  useEffect(() => {
    if (query.data && isValidPlanInfo(query.data)) {
      dispatch(setPlan(toPlanState(query.data)));
    }
  }, [query.data, dispatch]);

  return query;
}

async function mockPayments(): Promise<PaymentHistoryItem[]> {
  await new Promise((r) => setTimeout(r, 150));
  return [
    {
      _id: 'inv-001',
      invoiceCode: 'INV-2604-002',
      tier: 'starter',
      billingCycle: 'monthly',
      amount: 499_000,
      status: 'paid',
      paidAt: '2026-04-01T10:12:00Z',
      method: 'transfer',
    },
    {
      _id: 'inv-002',
      invoiceCode: 'INV-2603-045',
      tier: 'starter',
      billingCycle: 'monthly',
      amount: 499_000,
      status: 'paid',
      paidAt: '2026-03-01T09:45:00Z',
      method: 'momo',
    },
    {
      _id: 'inv-003',
      invoiceCode: 'INV-2602-119',
      tier: 'lite',
      billingCycle: 'monthly',
      amount: 0,
      status: 'paid',
      paidAt: '2026-02-01T08:00:00Z',
      method: 'cash',
    },
  ];
}

export function usePaymentHistory() {
  return useQuery({
    queryKey: planKeys.payments(),
    queryFn: () =>
      withMockFallback(() => planService.paymentHistory(), mockPayments, {
        label: 'plan.payments',
        validate: (v): v is PaymentHistoryItem[] => isArrayResponse(v),
      }),
    retry: 0,
  });
}

function emptyBillingPayments(page: number, limit: number): Promise<BillingPaymentsResponse> {
  return Promise.resolve({ data: [], meta: { page, limit, total: 0 } });
}

function isValidBillingPayments(v: unknown): v is BillingPaymentsResponse {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return Array.isArray(o.data) && typeof o.meta === 'object' && o.meta !== null;
}

export function useBillingPayments(page = 1, limit = 20) {
  return useQuery({
    queryKey: planKeys.billingPayments(page, limit),
    queryFn: () =>
      withMockFallback(
        () => planService.getBillingPayments(page, limit),
        () => emptyBillingPayments(page, limit),
        { label: 'billing.payments', validate: isValidBillingPayments },
      ),
    retry: 1,
  });
}
